"use client";

import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Calendar,
  ChevronRight,
  TrendingUp,
  Receipt,
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  Wallet,
  DollarSign,
  type LucideIcon,
} from "lucide-react";
import { PageWrapper, PageHeader, StatCard, TabBar, SectionCard, Button } from "@/components/ui";
import { recentTrades } from "@/lib/mock-data";
import type { Trade } from "@/lib/mock-data";
import {
  computeLedgerAccounting,
  computePeriodPnL,
  getWATDateKey,
  isToday,
  isWithinDays,
} from "@/lib/ledger-accounting";
import type { PeriodPnL } from "@/lib/ledger-accounting";
import { formatCurrency, formatDateCompact } from "@/lib/utils";
import { useToast } from "@/contexts/toast-context";
import { useScanner } from "@/hooks/use-scanner";
import type { Expense, ExpenseCategory, ExpenseSummary } from "@/lib/expenses-store";

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  bank_fees: "Bank Fees",
  gas_fees: "Gas / Network Fees",
  salaries: "Salaries & Wages",
  rent: "Rent",
  utilities: "Utilities",
  marketing: "Marketing",
  equipment: "Equipment",
  regulatory: "Regulatory / Compliance",
  other: "Other",
};

const CATEGORY_OPTIONS: ExpenseCategory[] = [
  "bank_fees", "gas_fees", "salaries", "rent",
  "utilities", "marketing", "equipment", "regulatory", "other",
];

const ease = [0.23, 1, 0.32, 1] as const;
const fadeIn = (d: number) => ({
  initial: { opacity: 0, y: 8 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: { duration: 0.4, ease, delay: d },
});

// ── Date range filter (matches trade ledger pattern) ──

const DATE_PRESETS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
] as const;

type DateRange = typeof DATE_PRESETS[number]["key"] | "custom";

function makeDateFilter(range: DateRange, customFrom: string, customTo: string): (dateStr: string) => boolean {
  if (range === "all") return () => true;
  if (range === "custom") {
    const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
    const to = customTo ? new Date(customTo + "T23:59:59") : null;
    if (!from && !to) return () => true;
    return (d) => {
      const date = new Date(d);
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    };
  }
  if (range === "today") return (d) => isToday(d);
  if (range === "7d") return (d) => isWithinDays(d, 7);
  if (range === "30d") return (d) => isWithinDays(d, 30);
  return () => true;
}

// ── Daily balance tracker (OTC Pulse pattern) ──

type DailySnapshot = {
  date: string;
  dateLabel: string;
  opening: number;   // USDT balance at 12 AM
  closing: number;   // USDT balance at next 12 AM
  totalIn: number;   // USDT bought
  totalOut: number;   // USDT sold
  inCount: number;
  outCount: number;
  avgInRate: number;  // avg NGN rate for buys
  avgOutRate: number; // avg NGN rate for sells
  turnover: number;   // total NGN moved
  tradeCount: number;
  lendBorrow: number; // net lend/borrow movement (positive = borrowed in, negative = lent out)
};

function computeDailySnapshots(trades: Trade[], currentBalance: number): DailySnapshot[] {
  const sorted = [...trades]
    .filter((t) => t.status !== "expired")
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const byDate = new Map<string, Trade[]>();
  for (const t of sorted) {
    const key = getWATDateKey(new Date(t.createdAt));
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key)!.push(t);
  }

  // First pass: compute each day's net movement (totalIn - totalOut + lendBorrow)
  type DayData = {
    date: string;
    totalIn: number; totalOut: number; inCount: number; outCount: number;
    avgInRate: number; avgOutRate: number; turnover: number;
    tradeCount: number; lendBorrow: number; netMovement: number;
  };

  const daysForward: DayData[] = [];
  for (const [date, dayTrades] of byDate) {
    let totalIn = 0, totalOut = 0, inCount = 0, outCount = 0;
    let ngnInTotal = 0, ngnOutTotal = 0, lendBorrow = 0;
    const inRates: number[] = [];
    const outRates: number[] = [];

    for (const t of dayTrades) {
      const qty = t.coinAmountSettled > 0 ? t.coinAmountSettled : t.usdtAmount;
      const ngn = t.nairaAmountSettled > 0 ? t.nairaAmountSettled : (t.ngnAmount || 0);

      if (t.direction === "buy") {
        totalIn += qty; inCount++;
        ngnOutTotal += ngn;
        if (t.rate) inRates.push(t.rate);
      } else if (t.direction === "sell") {
        totalOut += qty; outCount++;
        ngnInTotal += ngn;
        if (t.rate) outRates.push(t.rate);
      } else if (t.direction === "borrow") {
        lendBorrow += qty;
      } else if (t.direction === "return") {
        lendBorrow -= qty;
      } else if (t.direction === "lend") {
        lendBorrow -= qty;
      }
    }

    const netMovement = totalIn - totalOut + lendBorrow;
    daysForward.push({
      date, totalIn, totalOut, inCount, outCount, lendBorrow, netMovement,
      avgInRate: inRates.length > 0 ? inRates.reduce((a, b) => a + b, 0) / inRates.length : 0,
      avgOutRate: outRates.length > 0 ? outRates.reduce((a, b) => a + b, 0) / outRates.length : 0,
      turnover: ngnInTotal + ngnOutTotal,
      tradeCount: dayTrades.length,
    });
  }

  // Second pass: work backwards from current wallet balance
  // Today's closing = currentBalance
  // Each day's opening = closing - netMovement
  // Previous day's closing = this day's opening
  const days: DailySnapshot[] = [];
  let closing = currentBalance;

  for (let i = daysForward.length - 1; i >= 0; i--) {
    const dd = daysForward[i];
    const opening = closing - dd.netMovement;
    const d = new Date(dd.date + "T12:00:00");
    days.push({
      date: dd.date,
      opening: Math.max(0, opening),
      closing: Math.max(0, closing),
      totalIn: dd.totalIn, totalOut: dd.totalOut,
      inCount: dd.inCount, outCount: dd.outCount,
      avgInRate: dd.avgInRate, avgOutRate: dd.avgOutRate,
      turnover: dd.turnover, tradeCount: dd.tradeCount,
      lendBorrow: dd.lendBorrow,
      dateLabel: d.toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric", timeZone: "Africa/Lagos" }),
    });
    closing = opening; // previous day's closing = this day's opening
  }

  return days; // already newest first
}

function fmtUsdt(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNgn(n: number): string {
  return "₦" + Math.round(Math.abs(n)).toLocaleString("en-NG");
}

function fmtUsd(n: number): string {
  return "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Component ──

type ActiveTab = "overview" | "daily_tracker" | "expenses";

export default function AccountsPage() {
  const { toast } = useToast();
  const scanner = useScanner();
  const scannerTrades = scanner.scannerTrades;

  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");

  // Expense state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [expenseSummary, setExpenseSummary] = useState<ExpenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));
  const [formCategory, setFormCategory] = useState<ExpenseCategory>("bank_fees");
  const [formAmount, setFormAmount] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formRecurring, setFormRecurring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [perfPage, setPerfPage] = useState(0);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [walletUsdt, setWalletUsdt] = useState<number>(0);
  const PERF_PAGE_SIZE = 25;

  const allTrades = useMemo(() => [...recentTrades, ...scannerTrades], [scannerTrades]);
  const accounting = useMemo(() => computeLedgerAccounting(allTrades), [allTrades]);

  const dateFilter = useMemo(() => makeDateFilter(dateRange, customFrom, customTo), [dateRange, customFrom, customTo]);

  const periodPnL = useMemo(() => {
    if (dateRange === "all" && !customFrom && !customTo) return accounting.allTime;
    return computePeriodPnL(allTrades, accounting.trades, dateFilter);
  }, [dateRange, customFrom, customTo, allTrades, accounting, dateFilter]);

  const periodFiat = useMemo(() => {
    if (dateRange === "all" && !customFrom && !customTo) return accounting.fiat;
    const filtered = allTrades.filter((t) => t.status !== "expired" && dateFilter(t.createdAt));
    let ngnIn = 0, ngnOut = 0, pendingIn = 0, pendingOut = 0;
    for (const t of filtered) {
      const ngn = t.nairaAmountSettled > 0 ? t.nairaAmountSettled : (t.ngnAmount || 0);
      if (t.direction === "buy") {
        if (t.status === "settled") ngnOut += ngn; else pendingOut += t.ngnAmount || 0;
      } else if (t.direction === "sell") {
        if (t.status === "settled") ngnIn += ngn; else pendingIn += t.ngnAmount || 0;
      } else if (t.direction === "middleman" && t.status === "settled") {
        ngnIn += t.middlemanProfit ?? 0;
      }
    }
    return { ngnIn, ngnOut, netFlow: ngnIn - ngnOut, pendingIn, pendingOut };
  }, [dateRange, customFrom, customTo, allTrades, dateFilter]);

  const periodExpenses = useMemo(() => {
    if (dateRange === "all" && !customFrom && !customTo) return expenseSummary?.total ?? 0;
    return expenses.filter((e) => dateFilter(e.date)).reduce((sum, e) => sum + e.amount, 0);
  }, [dateRange, customFrom, customTo, expenses, expenseSummary, dateFilter]);

  const rateAnalysis = useMemo(() => [
    { label: "Today", pnl: accounting.today },
    { label: "This Week", pnl: computePeriodPnL(allTrades, accounting.trades, (d) => isWithinDays(d, 7)) },
    { label: "This Month", pnl: computePeriodPnL(allTrades, accounting.trades, (d) => isWithinDays(d, 30)) },
    { label: "All Time", pnl: accounting.allTime },
  ], [allTrades, accounting]);

  const dailySnapshots = useMemo(() => computeDailySnapshots(allTrades, walletUsdt), [allTrades, walletUsdt]);

  const tradePerformance = useMemo(() => {
    const sellTrades = allTrades
      .filter((t) => t.direction === "sell" && t.status !== "expired" && dateFilter(t.createdAt))
      .map((t) => ({ trade: t, acct: accounting.trades.get(t.id) }))
      .filter(({ acct }) => acct?.grossProfit != null)
      .sort((a, b) => new Date(b.trade.createdAt).getTime() - new Date(a.trade.createdAt).getTime());

    const winners = sellTrades.filter(({ acct }) => (acct!.grossProfit ?? 0) > 0);
    const losers = sellTrades.filter(({ acct }) => (acct!.grossProfit ?? 0) < 0);

    return {
      sellTrades, winners, losers,
      winTotal: winners.reduce((s, { acct }) => s + (acct!.grossProfit ?? 0), 0),
      lossTotal: losers.reduce((s, { acct }) => s + (acct!.grossProfit ?? 0), 0),
      bestWin: winners[0] || null,
      worstLoss: losers[losers.length - 1] || null,
    };
  }, [dateRange, customFrom, customTo, allTrades, accounting, dateFilter]);

  // Derived
  const grossProfit = periodPnL.grossProfit;
  const netProfit = grossProfit - periodExpenses;
  const totalExpenses = expenseSummary?.total ?? 0;
  const spread = (periodPnL.avgSellRate && periodPnL.avgBuyRate) ? periodPnL.avgSellRate - periodPnL.avgBuyRate : null;
  const perfTotalPages = Math.ceil(tradePerformance.sellTrades.length / PERF_PAGE_SIZE);
  const perfSlice = tradePerformance.sellTrades.slice(perfPage * PERF_PAGE_SIZE, (perfPage + 1) * PERF_PAGE_SIZE);

  // Expense CRUD
  const fetchExpenses = useCallback(() => {
    fetch("/api/expenses")
      .then((r) => r.json())
      .then((data) => { setExpenses(data.expenses || []); setExpenseSummary(data.summary || null); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  // Fetch real wallet balance for daily tracker
  useEffect(() => {
    fetch("/api/wallets/balances")
      .then((r) => r.json())
      .then((data) => setWalletUsdt(data.totalUsdt ?? 0))
      .catch(() => {});
  }, []);

  function resetForm() {
    setEditingId(null); setFormDate(new Date().toISOString().slice(0, 10));
    setFormCategory("bank_fees"); setFormAmount(""); setFormDescription("");
    setFormRecurring(false); setShowForm(false);
  }

  function startEdit(expense: Expense) {
    setEditingId(expense.id); setFormDate(expense.date.slice(0, 10));
    setFormCategory(expense.category); setFormAmount(String(expense.amount));
    setFormDescription(expense.description); setFormRecurring(expense.recurring); setShowForm(true);
  }

  async function handleSave() {
    if (!formAmount || !formDescription.trim()) { toast("Amount and description are required", "error"); return; }
    setSaving(true);
    try {
      const method = editingId ? "PUT" : "POST";
      const body: Record<string, unknown> = {
        date: formDate, category: formCategory, amount: parseFloat(formAmount),
        description: formDescription.trim(), recurring: formRecurring,
      };
      if (editingId) body.id = editingId;
      const res = await fetch("/api/expenses", { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || "Failed to save"); }
      toast(editingId ? "Expense updated" : "Expense added", "success"); resetForm(); fetchExpenses();
    } catch (e: unknown) { toast(e instanceof Error ? e.message : "Failed to save expense", "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(id: number) {
    setDeletingId(id);
    try {
      const res = await fetch(`/api/expenses?id=${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
      toast("Expense deleted", "success"); fetchExpenses();
    } catch { toast("Failed to delete expense", "error"); }
    finally { setDeletingId(null); }
  }

  return (
    <PageWrapper>
      <PageHeader
        title="Accounts"
        subtitle="P&L, expenses, and financial overview"
        actions={
          <div className="flex items-center gap-1">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => { setDateRange(p.key); setCustomFrom(""); setCustomTo(""); setPerfPage(0); }}
                className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all cursor-pointer ${
                  dateRange === p.key
                    ? "btn-pill-active text-accent"
                    : "text-muted hover:text-foreground hover:bg-surface-2"
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="relative">
              <button
                onClick={() => {
                  if (dateRange === "custom") {
                    setShowDatePicker((prev) => !prev);
                  } else {
                    setDateRange("custom");
                    setShowDatePicker(true);
                    setPerfPage(0);
                  }
                }}
                className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all cursor-pointer inline-flex items-center gap-1 ${
                  dateRange === "custom"
                    ? "btn-pill-active text-accent"
                    : "text-muted hover:text-foreground hover:bg-surface-2"
                }`}
              >
                <Calendar className="w-3 h-3" />
                {dateRange === "custom" && (customFrom || customTo)
                  ? `${customFrom || "..."} – ${customTo || "..."}`
                  : "Custom"}
              </button>
              <AnimatePresence>
                {showDatePicker && dateRange === "custom" && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 z-50 bg-surface rounded-lg p-4 shadow-lg"
                      style={{ border: "1px solid var(--border)" }}
                    >
                      <p className="text-[11px] font-bold text-muted uppercase tracking-wider mb-3">Custom Date Range</p>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2">
                          <span className="text-[12px] text-muted w-10">From</span>
                          <input
                            type="date"
                            value={customFrom}
                            onChange={(e) => { setCustomFrom(e.target.value); setPerfPage(0); }}
                            className="bg-surface-2 rounded-md px-2.5 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
                            style={{ border: "1px solid var(--border)", colorScheme: "dark" }}
                          />
                        </label>
                        <label className="flex items-center gap-2">
                          <span className="text-[12px] text-muted w-10">To</span>
                          <input
                            type="date"
                            value={customTo}
                            onChange={(e) => { setCustomTo(e.target.value); setPerfPage(0); }}
                            className="bg-surface-2 rounded-md px-2.5 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
                            style={{ border: "1px solid var(--border)", colorScheme: "dark" }}
                          />
                        </label>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <button
                          onClick={() => { setCustomFrom(""); setCustomTo(""); setPerfPage(0); }}
                          className="text-[11px] text-muted hover:text-foreground transition-colors cursor-pointer"
                        >
                          Clear dates
                        </button>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer"
                        >
                          Done
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        }
      />

      {/* Stat Cards */}
      <motion.div {...fadeIn(0)} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Turnover"
          value={`$${fmtUsdt(periodPnL.totalBought + periodPnL.totalSold)}`}
          secondary={`≈ ${formatCurrency(Math.round(periodFiat.ngnIn + periodFiat.ngnOut), "NGN")} · ${(periodPnL.buyCount + periodPnL.sellCount).toLocaleString()} trades`}
        />
        <StatCard
          label="Gross Profit"
          value={`${grossProfit >= 0 ? "+" : ""}${formatCurrency(Math.round(Math.abs(grossProfit)), "NGN")}`}
          tone={grossProfit >= 0 ? "credit" : "outstanding"}
          secondary={periodPnL.marginPct != null ? `${periodPnL.marginPct.toFixed(1)}% margin` : undefined}
        />
        <StatCard
          label="Expenses"
          value={formatCurrency(Math.round(periodExpenses), "NGN")}
          tone={periodExpenses > 0 ? "outstanding" : undefined}
        />
        <StatCard
          label="Net Profit"
          value={`${netProfit >= 0 ? "+" : ""}${formatCurrency(Math.round(Math.abs(netProfit)), "NGN")}`}
          tone={netProfit >= 0 ? "credit" : "outstanding"}
          secondary="after expenses"
        />
      </motion.div>

      {/* ═══ SUMMARY STATS — OTC Pulse style ═══ */}
      <motion.div {...fadeIn(0.05)} className="mb-6">
        <div className="bg-surface rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          {/* Header */}
          <div className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}>
            <h3 className="text-[13px] font-medium text-foreground">Summary Stats</h3>
          </div>

          {/* 2-column metric grid */}
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            <div className="grid grid-cols-2 divide-x" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
              <Metric icon={TrendingUp} label="Profit & Loss" value={fmtNgn(netProfit)} iconBg="bg-success/15" iconColor="text-success" />
              <Metric icon={Receipt} label="Tax Due" value="₦0.00" iconBg="bg-warning/15" iconColor="text-warning" />
            </div>
            <div className="grid grid-cols-2 divide-x" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
              <Metric icon={ArrowDownLeft} label="Asset In" value={`$${fmtUsdt(periodPnL.totalBought)}`}
                sub={`≈ ${fmtNgn(periodFiat.ngnOut)}`} iconBg="bg-accent/15" iconColor="text-accent" />
              <Metric icon={ArrowUpRight} label="Asset Out" value={`$${fmtUsdt(periodPnL.totalSold)}`}
                sub={`≈ ${fmtNgn(periodFiat.ngnIn)}`} iconBg="bg-danger/15" iconColor="text-danger" />
            </div>
            <div className="grid grid-cols-2 divide-x" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
              <Metric icon={Landmark} label="Fiat In" value={fmtNgn(periodFiat.ngnIn)} iconBg="bg-accent/15" iconColor="text-accent" />
              <Metric icon={Landmark} label="Fiat Out" value={fmtNgn(periodFiat.ngnOut)} iconBg="bg-danger/15" iconColor="text-danger" />
            </div>
            <div className="grid grid-cols-2 divide-x" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
              <Metric icon={Wallet} label="Expenses" value={fmtNgn(periodExpenses)} iconBg="bg-warning/15" iconColor="text-warning" />
              <Metric icon={DollarSign} label="Revenue" value={fmtNgn(grossProfit)} iconBg="bg-success/15" iconColor="text-success" />
            </div>
          </div>

          {/* Inventory Balance */}
          <div className="px-4 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-[11px] text-muted uppercase tracking-wider mb-1.5">Inventory Balance</p>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 bg-surface-2 rounded-md px-2 py-1 text-[12px] font-medium text-foreground"
                style={{ border: "1px solid var(--border)" }}>
                <span className="w-4 h-4 rounded-full bg-accent/20 flex items-center justify-center">
                  <span className="text-[8px] font-bold text-accent">$</span>
                </span>
                USDT
              </span>
              <span className="text-[15px] font-semibold text-foreground tabular-nums">
                USDT: {fmtUsdt(accounting.inventory.totalQuantity)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div {...fadeIn(0.08)} className="mb-6">
        <TabBar
          tabs={[
            { label: "Overview" },
            { label: "Daily Tracker" },
            { label: "Expenses", count: expenses.length },
          ]}
          active={activeTab === "overview" ? "Overview" : activeTab === "daily_tracker" ? "Daily Tracker" : "Expenses"}
          onChange={(tab) => setActiveTab(tab === "Overview" ? "overview" : tab === "Daily Tracker" ? "daily_tracker" : "expenses")}
        />
      </motion.div>

      {/* ═══ OVERVIEW ═══ */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          {/* P&L + Inventory side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Profit & Loss">
              <div className="px-4 py-3 space-y-0">
                <Row label="Revenue (Sell Proceeds)" value={formatCurrency(Math.round(periodPnL.grossRevenue), "NGN")} />
                {periodPnL.middlemanProfit > 0 && <Row label="Middleman Profit" value={formatCurrency(Math.round(periodPnL.middlemanProfit), "NGN")} muted />}
                <Row label="Cost of Sales (WAC)" value={`(${formatCurrency(Math.round(periodPnL.costOfSales), "NGN")})`} color="text-danger" />
                <Divider />
                <Row label="Gross Profit" value={formatCurrency(Math.round(Math.abs(grossProfit)), "NGN")}
                  color={grossProfit >= 0 ? "text-success" : "text-danger"} bold />
                <Row label="Gross Margin" value={`${periodPnL.marginPct?.toFixed(1) ?? "--"}%`} muted />
                {spread != null && <Row label="Spread" value={`${formatCurrency(Math.round(spread), "NGN")}/USDT`} muted />}
                <Divider />
                <Row label="Operating Expenses" value={`(${formatCurrency(Math.round(periodExpenses), "NGN")})`} color="text-danger" />
                <Divider />
                <Row label="Net Profit" value={`${netProfit >= 0 ? "+" : ""}${formatCurrency(Math.round(Math.abs(netProfit)), "NGN")}`}
                  color={netProfit >= 0 ? "text-success" : "text-danger"} bold />
              </div>
            </SectionCard>

            <SectionCard title="Inventory & Position">
              <div className="px-4 py-3 space-y-0">
                <Row label="USDT Owned" value={`${formatCurrency(Math.round(accounting.inventory.totalQuantity))} USDT`} />
                <Row label="Weighted Avg Cost" value={accounting.inventory.wac > 0 ? `${formatCurrency(Math.round(accounting.inventory.wac), "NGN")}/USDT` : "--"} />
                <Row label="Inventory at Cost" value={formatCurrency(Math.round(accounting.inventory.totalCost), "NGN")} />
                {accounting.inventory.uncostedQuantity > 0 && (
                  <Row label="Uncosted" value={`${formatCurrency(Math.round(accounting.inventory.uncostedQuantity))} USDT`} color="text-warning" />
                )}
                <Divider />
                <Row label="Borrowed (Liability)" value={accounting.position.borrowedUsdt > 0 ? `${formatCurrency(Math.round(accounting.position.borrowedUsdt))} USDT` : "--"} color="text-warning" />
                <Row label="Lent Out (Receivable)" value={accounting.position.lentUsdt > 0 ? `${formatCurrency(Math.round(accounting.position.lentUsdt))} USDT` : "--"} color="text-accent" />
                <Divider />
                <Row label="NGN Out (Buying)" value={formatCurrency(Math.round(periodFiat.ngnOut), "NGN")} color="text-danger" />
                <Row label="NGN In (Selling)" value={formatCurrency(Math.round(periodFiat.ngnIn), "NGN")} color="text-success" />
                <Row label="Net Cash Flow"
                  value={`${periodFiat.netFlow >= 0 ? "+" : ""}${formatCurrency(Math.round(Math.abs(periodFiat.netFlow)), "NGN")}`}
                  color={periodFiat.netFlow >= 0 ? "text-success" : "text-danger"} bold />
              </div>
            </SectionCard>
          </div>

          {/* Rate Analysis */}
          <SectionCard title="Rate Analysis">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[600px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-surface-2 border-b border-border">
                    <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Period</th>
                    <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Avg Buy</th>
                    <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Avg Sell</th>
                    <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Spread</th>
                    <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {rateAnalysis.map((ra) => {
                    const s = (ra.pnl.avgSellRate && ra.pnl.avgBuyRate) ? Math.round(ra.pnl.avgSellRate - ra.pnl.avgBuyRate) : null;
                    const count = ra.pnl.buyCount + ra.pnl.sellCount;
                    return (
                      <tr key={ra.label} className="border-b border-border hover:bg-surface-2/40 transition-colors">
                        <td className="px-2.5 py-3.5 text-[13px] text-foreground font-medium whitespace-nowrap">{ra.label}</td>
                        <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">{ra.pnl.avgBuyRate ? formatCurrency(Math.round(ra.pnl.avgBuyRate), "NGN") : "--"}</td>
                        <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">{ra.pnl.avgSellRate ? formatCurrency(Math.round(ra.pnl.avgSellRate), "NGN") : "--"}</td>
                        <td className={`px-2.5 py-3.5 text-right text-[13px] tabular-nums whitespace-nowrap ${s != null && s > 0 ? "text-success" : "text-muted"}`}>{s != null ? formatCurrency(Math.abs(s), "NGN") : "--"}</td>
                        <td className="px-2.5 py-3.5 text-right text-[13px] text-muted tabular-nums whitespace-nowrap">{count > 0 ? count : "--"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* Trade Performance */}
          {tradePerformance.sellTrades.length > 0 && (
            <SectionCard title="Trade Performance">
              {/* Summary row */}
              <div className="px-5 py-3 border-b border-border">
                <div className="flex items-center gap-6 text-[13px]">
                  <span className="text-muted">
                    {tradePerformance.sellTrades.length} sells
                  </span>
                  <span className="text-success tabular-nums">
                    {tradePerformance.winners.length} wins · +{formatCurrency(Math.round(tradePerformance.winTotal), "NGN")}
                  </span>
                  <span className="text-danger tabular-nums">
                    {tradePerformance.losers.length} losses · {formatCurrency(Math.round(tradePerformance.lossTotal), "NGN")}
                  </span>
                  {tradePerformance.bestWin && (
                    <span className="text-muted">
                      Best: <span className="text-success">+{formatCurrency(Math.round(tradePerformance.bestWin.acct!.grossProfit!), "NGN")}</span>
                      {" "}<span className="text-muted/60">{(tradePerformance.bestWin.trade.counterpartyName || "").toUpperCase()}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[800px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-surface-2 border-b border-border">
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Date</th>
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Trade</th>
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Counterparty</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Qty</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Cost@WAC</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Sell@</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">P&L</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Group paginated trades by date for date group headers
                      const rows: React.ReactNode[] = [];
                      let lastDateKey = "";
                      for (const { trade, acct } of perfSlice) {
                        const dateKey = trade.createdAt ? trade.createdAt.slice(0, 10) : "";
                        if (dateKey && dateKey !== lastDateKey) {
                          const d = new Date(dateKey + "T12:00:00");
                          const dateLabel = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" });
                          rows.push(
                            <tr key={`dg-${dateKey}`}>
                              <td colSpan={8} className="px-5 py-2.5 bg-surface-2 border-b border-border">
                                <span className="text-[11px] font-bold text-foreground/70 uppercase tracking-wider">{dateLabel}</span>
                              </td>
                            </tr>
                          );
                          lastDateKey = dateKey;
                        }
                        rows.push(
                          <tr key={trade.id} className="border-b border-border hover:bg-surface-2/40 cursor-pointer group transition-colors"
                            onClick={() => { window.location.href = `/ledger?trade=${trade.id}&range=all`; }}>
                            <td className="px-2.5 py-3.5 text-[13px] text-muted tabular-nums whitespace-nowrap">{formatDateCompact(trade.createdAt)}</td>
                            <td className="px-2.5 py-3.5 text-[13px] text-muted tabular-nums whitespace-nowrap">#{trade.id}</td>
                            <td className="px-2.5 py-3.5 text-[13px] text-foreground whitespace-nowrap">{(trade.counterpartyName || "Unknown").toUpperCase()}</td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">{fmtUsdt(acct?.costedQty ?? trade.usdtAmount)}</td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">{acct?.wacAtSale ? formatCurrency(Math.round(acct.wacAtSale), "NGN") : "--"}</td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">{trade.rate ? formatCurrency(Math.round(trade.rate), "NGN") : "--"}</td>
                            <td className={`px-2.5 py-3.5 text-right text-[13px] font-medium tabular-nums whitespace-nowrap ${(acct?.grossProfit ?? 0) >= 0 ? "text-success" : "text-danger"}`}>
                              {(acct?.grossProfit ?? 0) >= 0 ? "+" : ""}{formatCurrency(Math.round(Math.abs(acct?.grossProfit ?? 0)), "NGN")}
                            </td>
                            <td className={`px-2.5 py-3.5 text-right text-[13px] tabular-nums whitespace-nowrap ${(acct?.marginPct ?? 0) >= 0 ? "text-success" : "text-danger"}`}>
                              {acct?.marginPct != null ? `${acct.marginPct >= 0 ? "+" : ""}${acct.marginPct.toFixed(1)}%` : "--"}
                            </td>
                          </tr>
                        );
                      }
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
              {perfTotalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                  <span className="text-xs text-muted">
                    {perfPage * PERF_PAGE_SIZE + 1}&ndash;{Math.min((perfPage + 1) * PERF_PAGE_SIZE, tradePerformance.sellTrades.length)} of {tradePerformance.sellTrades.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" disabled={perfPage === 0} onClick={() => setPerfPage((p) => p - 1)}>Prev</Button>
                    <span className="text-xs text-muted tabular-nums">Page {perfPage + 1} of {perfTotalPages}</span>
                    <Button variant="secondary" size="sm" disabled={perfPage >= perfTotalPages - 1} onClick={() => setPerfPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* Expense Breakdown */}
          {expenseSummary && totalExpenses > 0 && (
            <SectionCard title="Expense Breakdown">
              <div className="px-4 py-3 space-y-0">
                {CATEGORY_OPTIONS
                  .filter((cat) => (expenseSummary.byCategory[cat] || 0) > 0)
                  .sort((a, b) => (expenseSummary.byCategory[b] || 0) - (expenseSummary.byCategory[a] || 0))
                  .map((cat) => {
                    const amount = expenseSummary.byCategory[cat] || 0;
                    const pct = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                    return <Row key={cat} label={`${CATEGORY_LABELS[cat]} (${pct.toFixed(0)}%)`} value={formatCurrency(Math.round(amount), "NGN")} />;
                  })}
                <Divider />
                <Row label="Total" value={formatCurrency(Math.round(totalExpenses), "NGN")} color="text-danger" bold />
              </div>
              <div className="px-4 pb-3">
                <button onClick={() => setActiveTab("expenses")}
                  className="text-[12px] text-accent hover:text-accent/80 font-medium cursor-pointer transition-colors">
                  View all expenses
                </button>
              </div>
            </SectionCard>
          )}

        </div>
      )}

      {/* ═══ DAILY TRACKER ═══ */}
      {activeTab === "daily_tracker" && (
        <div>
          {dailySnapshots.length === 0 ? (
            <div className="bg-surface rounded-lg p-12 text-center" style={{ border: "1px solid var(--border)" }}>
              <p className="text-muted text-sm">No trading activity yet</p>
            </div>
          ) : (
            <SectionCard>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[700px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-surface-2 border-b border-border">
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Date</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Opening</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Closing</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Lend/Borrow</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Turnover</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Trades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySnapshots.slice(0, 30).map((day) => {
                      const isExpanded = expandedDay === day.date;
                      const delta = day.closing - day.opening;
                      return (
                        <Fragment key={day.date}>
                          <tr
                            className="border-b border-border hover:bg-surface-2/40 cursor-pointer group transition-colors"
                            onClick={() => setExpandedDay(isExpanded ? null : day.date)}
                          >
                            <td className="px-2.5 py-3.5 text-[13px] text-foreground font-medium whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5">
                                <ChevronRight className={`w-3 h-3 text-muted transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                                {day.dateLabel}
                              </span>
                            </td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-muted tabular-nums whitespace-nowrap">${fmtUsdt(day.opening)}</td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] tabular-nums whitespace-nowrap">
                              <span className="text-foreground font-medium">${fmtUsdt(day.closing)}</span>
                              {delta !== 0 && (
                                <span className={`ml-1.5 text-[11px] ${delta > 0 ? "text-success" : "text-danger"}`}>
                                  {delta > 0 ? "+" : ""}{fmtUsdt(delta)}
                                </span>
                              )}
                            </td>
                            <td className={`px-2.5 py-3.5 text-right text-[13px] tabular-nums whitespace-nowrap ${day.lendBorrow !== 0 ? (day.lendBorrow > 0 ? "text-warning" : "text-accent") : "text-muted"}`}>
                              {day.lendBorrow !== 0
                                ? `${day.lendBorrow > 0 ? "+" : ""}$${fmtUsdt(day.lendBorrow)}`
                                : "--"}
                            </td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-muted tabular-nums whitespace-nowrap">{day.turnover > 0 ? formatCurrency(Math.round(day.turnover), "NGN") : "--"}</td>
                            <td className="px-2.5 py-3.5 text-right text-[13px] text-muted tabular-nums whitespace-nowrap">{day.tradeCount}</td>
                          </tr>
                          {/* Expanded detail */}
                          {isExpanded && (
                            <tr className="bg-surface-2/30">
                              <td colSpan={6} className="px-5 py-3">
                                <div className="flex items-center gap-8 text-[13px]">
                                  {day.inCount > 0 && (
                                    <div>
                                      <span className="text-muted">Total In: </span>
                                      <span className="text-success font-medium tabular-nums">${fmtUsdt(day.totalIn)}</span>
                                      <span className="text-muted"> ({day.inCount} txns)</span>
                                      {day.avgInRate > 0 && (
                                        <span className="text-muted"> · avg {formatCurrency(Math.round(day.avgInRate), "NGN")}</span>
                                      )}
                                    </div>
                                  )}
                                  {day.outCount > 0 && (
                                    <div>
                                      <span className="text-muted">Total Out: </span>
                                      <span className="text-danger font-medium tabular-nums">${fmtUsdt(day.totalOut)}</span>
                                      <span className="text-muted"> ({day.outCount} txns)</span>
                                      {day.avgOutRate > 0 && (
                                        <span className="text-muted"> · avg {formatCurrency(Math.round(day.avgOutRate), "NGN")}</span>
                                      )}
                                    </div>
                                  )}
                                  {day.inCount === 0 && day.outCount === 0 && (
                                    <span className="text-muted">No buy/sell trades this day</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <span className="text-xs text-muted">{Math.min(30, dailySnapshots.length)} of {dailySnapshots.length} days</span>
                <span className="text-xs text-muted tabular-nums">
                  ${fmtUsdt(dailySnapshots[dailySnapshots.length - 1]?.opening ?? 0)}
                  {" → "}
                  <span className="text-foreground font-medium">${fmtUsdt(dailySnapshots[0]?.closing ?? 0)}</span>
                </span>
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* ═══ EXPENSES ═══ */}
      {activeTab === "expenses" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[13px] text-muted">
              {expenses.length} expense{expenses.length !== 1 ? "s" : ""}
              {expenseSummary ? ` · ${formatCurrency(Math.round(expenseSummary.total), "NGN")}` : ""}
            </p>
            <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowForm(true); }}>
              <Plus className="w-3.5 h-3.5" /> Add Expense
            </Button>
          </div>

          <AnimatePresence>
            {showForm && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease }} className="overflow-hidden mb-4">
                <div className="bg-surface rounded-lg p-5" style={{ border: "1px solid var(--border)" }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[13px] font-medium text-foreground">{editingId ? "Edit Expense" : "Add Expense"}</h3>
                    <button onClick={resetForm} className="p-1 rounded-md text-muted hover:text-foreground transition-colors cursor-pointer"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="text-[11px] text-muted uppercase tracking-wider block mb-1.5">Date</label>
                      <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)}
                        className="w-full bg-surface-2 rounded-md px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)", colorScheme: "dark" }} />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted uppercase tracking-wider block mb-1.5">Category</label>
                      <select value={formCategory} onChange={(e) => setFormCategory(e.target.value as ExpenseCategory)}
                        className="w-full bg-surface-2 rounded-md px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)", colorScheme: "dark" }}>
                        {CATEGORY_OPTIONS.map(cat => (<option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-muted uppercase tracking-wider block mb-1.5">Amount (NGN)</label>
                      <input type="number" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} placeholder="0" min="0" step="any"
                        className="w-full bg-surface-2 rounded-md px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30 tabular-nums"
                        style={{ border: "1px solid var(--border)" }} />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted uppercase tracking-wider block mb-1.5">Description</label>
                      <input type="text" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="e.g. Transfer fee to Kuda"
                        className="w-full bg-surface-2 rounded-md px-3 py-2 text-[13px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)" }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <label className="flex items-center gap-2 text-[12px] text-muted cursor-pointer">
                      <input type="checkbox" checked={formRecurring} onChange={(e) => setFormRecurring(e.target.checked)} className="rounded" />
                      Recurring expense
                    </label>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={resetForm}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : editingId ? "Update" : "Add"}</Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <div className="text-center py-12 text-muted text-[13px]">Loading expenses...</div>
          ) : expenses.length === 0 ? (
            <div className="bg-surface rounded-lg p-12 text-center" style={{ border: "1px solid var(--border)" }}>
              <p className="text-muted text-[13px] mb-1">No expenses recorded yet</p>
              <p className="text-muted/60 text-[12px]">Add bank fees, salaries, gas fees, and other costs to calculate net profit.</p>
            </div>
          ) : (
            <SectionCard>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[600px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-surface-2 border-b border-border">
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Date</th>
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Category</th>
                      <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Description</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3">Amount</th>
                      <th className="text-right text-[13px] font-semibold text-muted px-2.5 py-3 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((exp) => (
                      <tr key={exp.id} className="border-b border-border hover:bg-surface-2/40 transition-colors group">
                        <td className="px-2.5 py-3.5 text-[13px] text-muted tabular-nums whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3 text-muted/50" />
                            {new Date(exp.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </td>
                        <td className="px-2.5 py-3.5 text-[13px] whitespace-nowrap">
                          <span className="text-foreground">{CATEGORY_LABELS[exp.category]}</span>
                          {exp.recurring && <span className="ml-1.5 text-[9px] font-bold text-accent bg-accent/10 px-1 py-0.5 rounded uppercase">Recurring</span>}
                        </td>
                        <td className="px-2.5 py-3.5 text-[13px] text-foreground">{exp.description}</td>
                        <td className="px-2.5 py-3.5 text-right text-[13px] text-danger font-medium tabular-nums whitespace-nowrap">{formatCurrency(Math.round(exp.amount), "NGN")}</td>
                        <td className="px-2.5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => startEdit(exp)} className="p-1 text-muted hover:text-foreground transition-colors rounded cursor-pointer" title="Edit">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDelete(exp.id)} disabled={deletingId === exp.id}
                              className="p-1 text-muted hover:text-danger transition-colors rounded cursor-pointer" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <span className="text-xs text-muted">Total Expenses</span>
                <span className="text-[13px] font-medium text-danger tabular-nums">{formatCurrency(Math.round(totalExpenses), "NGN")}</span>
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </PageWrapper>
  );
}

// ── Metric cell for Summary Stats grid ──

function Metric({ icon: Icon, label, value, sub, iconBg, iconColor }: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-5 h-5 rounded-full flex items-center justify-center ${iconBg}`}>
          <Icon className={`w-3 h-3 ${iconColor}`} />
        </span>
        <span className="text-[12px] text-muted">{label}</span>
      </div>
      <p className="text-[17px] font-semibold text-foreground tabular-nums tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted tabular-nums mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Row: label-value pair for detail sections ──

function Row({ label, value, color, bold, muted: isMuted }: {
  label: string; value: string; color?: string; bold?: boolean; muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-[5px]">
      <span className={`text-[13px] ${isMuted ? "text-muted/60 pl-3" : "text-muted"}`}>{label}</span>
      <span className={`text-[13px] tabular-nums ${bold ? "font-medium" : ""} ${color || "text-foreground"}`}>{value}</span>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-border/30 my-1" />;
}
